import React from "react";

import { separateInputWrapperPropsAndInputProps } from "../../../utils";

import { RadioButtons, RadioButtonsProps } from "../../../../../molecules/RadioButtons";
import { usePyForm } from "../../../context";
import { BaseFormFieldProps, FormField } from "../../FormComponents/FormField";

// Get all unique props from RadioButtons that aren't
type UniqueInputProps<T> = Omit<RadioButtonsProps<T>, keyof BaseFormFieldProps<T> | "value" | "onChange">;

export type FormRadioButtonsProps<T> = Omit<BaseFormFieldProps<T>, "input"> & UniqueInputProps<T>;

/**
 * This is a form component that is used to create a radio button group.
 * It is a wrapper around the RadioButtons component.
 * It is used to create a radio button group that is connected to the PyForm context.
 * Its props cross-cut with the RadioButtons component and the FormField component.
 *
 * RadioButtons is a group of radio buttons and their labels
 */
export const FormRadioButtons = <T extends string>(props: FormRadioButtonsProps<T>) => {
	const form = usePyForm();
	const { inputWrapperProps, inputProps } = separateInputWrapperPropsAndInputProps(props, form.id);

	return (
		<FormField<T>
			{...inputWrapperProps}
			input={(field) => (
				<RadioButtons
					{...inputProps}
					options={props.options}
					value={field.value}
					onChange={field.onChange}
					id={inputWrapperProps.id}
					data-testid={`${inputWrapperProps["data-testid"]}-radio-buttons`}
					disabled={inputWrapperProps.disabled}
				/>
			)}
		/>
	);
};

export default FormRadioButtons;
